/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  NoteAdd,
  PersonAdd,
  Close,
  EventNote,
  TimerOff,
  Today,
  MarkEmailUnread,
  HistoryToggleOff,
  AssignmentTurnedIn,
  Download
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IStoreState } from "../../../redux/initialStoreState";
import { LoadState } from "../../../constants/enums";
import { useDispatchWrapper } from "../../../hooks";
import { fetchLeadStatusCountAsync } from "../../../redux/leads/leadsActions";
import { useBillingCompanyContext } from "../../../providers";
import { useDateFilter } from "../../../components/Table/hooks/useDateFilter";
import { api } from "../../../api/api";
import { formatText } from "../../../helpers";
import { EditCustomerRightPanel } from "../../customers/EditCustomer";
import { clearStudent } from "../../../redux/student/studentActions";
import { LeadsTableByCount } from "../Dashboard/LeadsTableByCount";
import { ChatLeads } from "../../chatLeads/Chatleads";
import { EventPerformanceReport } from "../MainDashboard/components/EventPerformanceReport";
import { CancelationReasonReport } from "../MainDashboard/components/CancelationReasonReport";
import { VenueReport } from "../MainDashboard/components/VenueReport";
import { MobileKpiCard } from "./MobileKpiCard";
import { MobileDashboardSection } from "./MobileDashboardSection";
import { RightPanel } from "../../../components/RightPanel";

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; accent: string }
> = {
  ONGOING: {
    label: "Ongoing Leads",
    icon: <EventNote fontSize="small" />,
    accent: "#1a97f5",
  },
  MISSED_FOLLOWUPS: {
    label: "Missed Followups",
    icon: <TimerOff fontSize="small" />,
    accent: "#e46a76",
  },
  TODAY_FOLLOWUPS: {
    label: "Today's Followups",
    icon: <Today fontSize="small" />,
    accent: "#0bb2fb",
  },
  TODAYS_NEW_EXTERNAL_LEADS: {
    label: "New WhatsApp Leads",
    icon: <MarkEmailUnread fontSize="small" />,
    accent: "#25D366",
  },
  EXTERNAL_LEAD_MISSED_FOLLOWUPS: {
    label: "External Missed",
    icon: <HistoryToggleOff fontSize="small" />,
    accent: "#f59e0b",
  },
  EXTERNAL_LEAD_TODAY_FOLLOWUPS: {
    label: "External Today",
    icon: <AssignmentTurnedIn fontSize="small" />,
    accent: "#8b5cf6",
  },
};

export const MobileOverviewContent: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const [openByParam, setOpenByParam] = useState<string>("");
  const [triggerDownload, setTriggerDownload] = useState<number>(0);
  const [openEditCustomer, setOpenEditCustomer] = useState<boolean>(false);
  const [chatExternalCounts, setChatExternalCounts] = useState<
    Record<string, number>
  >({});
  const [chatExternalLoading, setChatExternalLoading] =
    useState<boolean>(false);

  const { list: recordsCounts, loading } = useSelector(
    (storeState: IStoreState) => storeState.leads.recordCounts,
  );

  const { date } = useDateFilter({ defaultType: "today" });
  const companyOutlet = useBillingCompanyContext();
  const dateState = useMemo(() => ({ dates: date }), [date]);
  const [monthType] = useState<string>("Enquiry_Month");

  // Fetch lead status counts
  useEffect(() => {
    if (companyOutlet.comapny.company_outlet_uuid) {
      dispatch(
        fetchLeadStatusCountAsync(companyOutlet.comapny.company_outlet_uuid),
      );
    }
  }, [date, dispatch, companyOutlet.comapny.company_outlet_uuid]);

  // Fetch external chat counts
  useEffect(() => {
    const fetchChatExternalCounts = async () => {
      try {
        setChatExternalLoading(true);
        const res = await api.get("/leads/get-status-chat-external-leads", {
          params: {
            type: "count",
            company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
          },
        });
        setChatExternalCounts(res.data?.data?.statusCounts || {});
      } catch (err) {
        setChatExternalCounts({});
      } finally {
        setChatExternalLoading(false);
      }
    };

    if (companyOutlet.comapny.company_outlet_uuid) {
      fetchChatExternalCounts();
    }
  }, [date, companyOutlet.comapny.company_outlet_uuid]);

  const isExternalLeadStatus = useCallback((status: string) => {
    return (
      status === "TODAYS_NEW_EXTERNAL_LEADS" ||
      status === "EXTERNAL_LEAD_MISSED_FOLLOWUPS" ||
      status === "EXTERNAL_LEAD_TODAY_FOLLOWUPS"
    );
  }, []);

  const leadStatuses = [
    "ONGOING",
    "MISSED_FOLLOWUPS",
    "TODAY_FOLLOWUPS",
    "TODAYS_NEW_EXTERNAL_LEADS",
    "EXTERNAL_LEAD_MISSED_FOLLOWUPS",
    "EXTERNAL_LEAD_TODAY_FOLLOWUPS",
  ];

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      {/* Action Buttons Row */}
      <Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<NoteAdd fontSize="small" />}
          onClick={() => navigate("/event-leads/manage")}
          sx={{
            py: 1.25,
            borderRadius: "14px",
            backgroundColor: "#027302",
            "&:hover": { backgroundColor: "#025902" },
            fontWeight: 700,
            fontSize: "0.85rem",
            boxShadow: "0 4px 14px rgba(2, 115, 2, 0.25)",
            textTransform: "none",
          }}
        >
          Create Lead
        </Button>
        <Button
          fullWidth
          variant="contained"
          startIcon={<PersonAdd fontSize="small" />}
          onClick={() => setOpenEditCustomer(true)}
          sx={{
            py: 1.25,
            borderRadius: "14px",
            backgroundColor: "#0D3B66",
            "&:hover": { backgroundColor: "#082642" },
            fontWeight: 700,
            fontSize: "0.85rem",
            boxShadow: "0 4px 14px rgba(13, 59, 102, 0.25)",
            textTransform: "none",
          }}
        >
          Add Guest
        </Button>
      </Stack>

      {/* KPI Cards Section */}
      <MobileDashboardSection
        title={
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: "1.3rem" }}>
            Lead Overview
          </Typography>
        }
        disableCard
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
            width: "100%",
          }}
        >
          {leadStatuses.map((status) => {
            const isExternal = isExternalLeadStatus(status);
            const count = isExternal
              ? chatExternalCounts?.[status] || 0
              : (recordsCounts as any)?.[status] || 0;
            const isLoading = isExternal
              ? chatExternalLoading
              : loading === LoadState.InProgress;

            const config = STATUS_CONFIG[status] || {
              label: formatText(status),
              icon: null,
              accent: "#1a97f5",
            };

            const isSelected = openByParam === status;

            return (
              <MobileKpiCard
                key={status}
                label={config.label}
                count={Number(count)}
                loading={isLoading}
                icon={config.icon}
                accentColor={config.accent}
                active={isSelected}
                onClick={() => {
                  setOpenByParam((prev) => (prev === status ? "" : status));
                }}
              />
            );
          })}
        </Box>
      </MobileDashboardSection>

      {/* Detailed Leads List (Opens in RightPanel Overlay when a KPI card is clicked) */}
      <RightPanel
        open={Boolean(openByParam)}
        onClose={() => setOpenByParam("")}
        heading={STATUS_CONFIG[openByParam]?.label || formatText(openByParam)}
        topRightButtons={
          !isExternalLeadStatus(openByParam) && (Number((recordsCounts as any)?.[openByParam] || 0) > 0) ? (
            <IconButton onClick={() => setTriggerDownload((p) => p + 1)} sx={{ p: 0, mr: 1 }}>
              <Download sx={{ color: "#fff", fontSize: "20px" }} />
            </IconButton>
          ) : undefined
        }
        width="100%"
        disablePadding
      >
        {Boolean(openByParam) && (
          <Box sx={{ width: "100%", p: 1, pb: 4 }}>
            {isExternalLeadStatus(openByParam) ? (
              <ChatLeads statusParam={openByParam} hideFilters />
            ) : (
              <LeadsTableByCount
                statusParam={openByParam}
                monthType={monthType}
                dateState={dateState}
                whatsappStatus={false}
                triggerDownload={triggerDownload}
              />
            )}
          </Box>
        )}
      </RightPanel>

      {/* Performance Reports (Event performance, cancellations, venues) */}
      <EventPerformanceReport frameless />
      <CancelationReasonReport frameless />
      <VenueReport frameless />

      {/* Add Guest Modal */}
      {openEditCustomer && (
        <EditCustomerRightPanel
          open={true}
          onClose={() => {
            setOpenEditCustomer(false);
            dispatch(clearStudent());
          }}
          onComplete={() => {
            setOpenEditCustomer(false);
            dispatch(clearStudent());
          }}
        />
      )}
    </Stack>
  );
};
